import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  FacadeSiteConfigurations,
  InspectionSiteInfo,
  kFaultFreeSiteCode,
  kFrontRecordsSiteCode,
  kRearRecordsSiteCode,
} from '@euler/model';
import { AbnormalLevel } from '@euler/model/enum';
import { SiteInspectionStaged } from '@euler/model/task-detail';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { memo, useCallback, useMemo, useRef } from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
  findNodeHandle,
} from 'react-native';
import {
  FacadeIllustrationView,
  useFacadeIllustrationViewHeightByWidth,
} from './FacadeIllustrationView';
import { FacadeSitesTip, kFacadeSitesTipLineHeight } from './FacadeSitesTip';

const kDefaultRecordButtonHeight = 28;
const kDefaultIllustrationViewMargin = 15;
const kContainerPaddingBottom = 10;

export const useFacadeSitesViewHeightByContentWidth = (
  contentWidth: number,
  options?: {
    recordButtonHeight?: number;
    illustrationViewMargin?: number;
  },
) => {
  const { rpx } = useResponsiveContext();
  const recordButtonHeight =
    options?.recordButtonHeight ?? rpx(kDefaultRecordButtonHeight);
  const illustrationViewMargin =
    options?.illustrationViewMargin ?? rpx(kDefaultIllustrationViewMargin);
  return (
    rpx(15) /* front record button margin top */ +
    recordButtonHeight /* front record button height */ +
    rpx(10) /* front record button margin bottom */ +
    useFacadeIllustrationViewHeightByWidth(
      contentWidth - illustrationViewMargin * 2,
    ) /* illustration view height */ +
    rpx(10) /* rear record button margin top */ +
    recordButtonHeight /* front record button height */ +
    rpx(15) /* front record button margin bottom */ +
    rpx(kFacadeSitesTipLineHeight) /* tip text height */ +
    rpx(kContainerPaddingBottom) /* container padding bottom */
  );
};

export const FacadeSitesView = memo(
  ({
    noBundle,
    facadeSites,
    siteInspections,
    frontRecordCount,
    rearRecordCount,
    contentWidth,
    style,
    illustrationViewMargin,
    readonly,
    requiredSites,
    requiredSitesLabel,
    onSitePress,
  }: {
    noBundle?: boolean;
    facadeSites: InspectionSiteInfo[] | undefined;
    siteInspections: Map<number, SiteInspectionStaged>;
    frontRecordCount: number;
    rearRecordCount: number;
    contentWidth: number;
    style?: StyleProp<ViewStyle>;
    illustrationViewMargin?: number;
    readonly?: boolean;
    /** Sites the template makes mandatory, called out below the how-to. */
    requiredSites?: InspectionSiteInfo[];
    /** Leads the required-sites line; hosts word it for their own flow. */
    requiredSitesLabel?: string;
    onSitePress?: (code: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    illustrationViewMargin =
      illustrationViewMargin ?? rpx(kDefaultIllustrationViewMargin);
    const siteCodeSetWithDefect = useMemo<Set<string>>(() => {
      return new Set(
        [...siteInspections.values()]
          .filter(
            x =>
              (noBundle || x.bundle === 'facade') &&
              x.items.some(t => t.abnormalLevel !== AbnormalLevel.Fine),
          )
          .map(x => x.siteCode),
      );
    }, [siteInspections, noBundle]);

    const nonPredefinedFacadeSites = useMemo(() => {
      const predefinedSiteCodeSet = new Set([
        ...FacadeSiteConfigurations.map(x => x.code),
        kFaultFreeSiteCode,
        kFrontRecordsSiteCode,
        kRearRecordsSiteCode,
      ]);
      return (facadeSites ?? []).filter(
        x => !predefinedSiteCodeSet.has(x.code),
      );
    }, [facadeSites]);

    const moreButtonRef = useRef<any>(null);
    const { showActionSheetWithOptions } = useActionSheet();

    const onMorePress = useCallback(() => {
      if (nonPredefinedFacadeSites.length === 0) {
        return;
      }
      const anchor = findNodeHandle(moreButtonRef.current) ?? undefined;
      showActionSheetWithOptions(
        {
          title: '',
          options: [...nonPredefinedFacadeSites.map(x => x.name), '返回'],
          cancelButtonIndex: nonPredefinedFacadeSites.length,
          anchor,
        },
        index => {
          if (
            index == null ||
            index < 0 ||
            index >= nonPredefinedFacadeSites.length
          ) {
            return;
          }
          onSitePress?.(nonPredefinedFacadeSites[index].code);
        },
      );
    }, [nonPredefinedFacadeSites, onSitePress, showActionSheetWithOptions]);

    return (
      <View
        css={`
          background-color: #fff;
          padding-bottom: ${rpx(kContainerPaddingBottom)}px;
        `}
        style={style}
      >
        {/* top container */}
        <Center
          css={`
            margin-top: ${rpx(15)}px;
            margin-bottom: ${rpx(10)}px;
          `}
        >
          {/* fault-free record button */}
          <View
            style={StyleSheet.absoluteFill}
            pointerEvents="box-none"
            css={`
              flex-direction: row;
              justify-content: flex-start;
              align-items: center;
            `}
          >
            <FacadeRecordCameraButton
              text="无异常记录"
              siteCode={kFaultFreeSiteCode}
              recordCount={0}
              onPress={onSitePress}
            />
          </View>

          {/* front record button */}
          <FacadeRecordCameraButton
            text="前部记录"
            siteCode={kFrontRecordsSiteCode}
            recordCount={frontRecordCount}
            onPress={onSitePress}
          />

          {/* other sites action button */}
          {nonPredefinedFacadeSites.length > 0 && (
            <View
              style={StyleSheet.absoluteFill}
              pointerEvents="box-none"
              css={`
                flex-direction: row;
                justify-content: flex-end;
                align-items: center;
              `}
            >
              <TouchableOpacity
                css={`
                  flex-direction: row;
                  justify-content: flex-end;
                  align-items: center;
                `}
                onPress={onMorePress}
                ref={moreButtonRef}
              >
                <Typography.Label size={rpx(14)} color={'#999'} light>
                  其他
                </Typography.Label>
                <Icon name="more-vertical" size={rpx(15)} color={'#999'} />
              </TouchableOpacity>
            </View>
          )}
        </Center>

        {/* facade illustration view */}
        <FacadeIllustrationView
          sites={facadeSites ?? []}
          size={contentWidth - illustrationViewMargin * 2}
          siteCodeSetWithDefect={siteCodeSetWithDefect}
          onSitePress={onSitePress}
        />

        {/* rear record button */}
        <Center
          css={`
            margin-top: ${rpx(10)}px;
            margin-bottom: ${rpx(15)}px;
          `}
        >
          <FacadeRecordCameraButton
            text="后部记录"
            siteCode={kRearRecordsSiteCode}
            recordCount={rearRecordCount}
            onPress={onSitePress}
          />
        </Center>

        {/* tip text */}
        {!readonly ? (
          <FacadeSitesTip
            requiredSites={requiredSites}
            requiredSitesLabel={requiredSitesLabel}
          />
        ) : null}
      </View>
    );
  },
);

const FacadeRecordCameraButton = memo(
  ({
    text,
    style,
    siteCode,
    recordCount,
    onPress,
  }: {
    text: string;
    style?: StyleProp<ViewStyle>;
    siteCode: string;
    recordCount: number;
    onPress?: (siteCode: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const handlePress = useCallback(() => {
      onPress?.(siteCode);
    }, [onPress, siteCode]);

    return (
      <TouchableOpacity
        css={`
          height: ${rpx(kDefaultRecordButtonHeight)}px;
          padding: 0px ${rpx(10)}px;
          border-radius: ${rpx(5)}px;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          background-color: #f4f3f9;
        `}
        style={style}
        onPress={handlePress}
      >
        <Icon name="camera-fill" color="#999" size={rpx(16)} />
        <Typography.Label
          light
          color="#999"
          size={rpx(14)}
          css={`
            margin-left: ${rpx(5)}px;
          `}
        >
          {recordCount > 0 ? `${text} (${recordCount})` : text}
        </Typography.Label>
      </TouchableOpacity>
    );
  },
);
